<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);

const report = inject<{ data: Record<string, unknown>[] }>("report", { data: [] });

const dataKey = field<string>("dataKey", "");
const labelKey = field<string>("labelKey", "name");
const valueKey = field<string>("valueKey", "value");
const showLegend = field<boolean>("showLegend", true);
const showLabels = field<boolean>("showLabels", true);
const showTotal = field<boolean>("showTotal", false);
const innerRadius = field<number>("innerRadius", 70);
const padAngle = field<number>("padAngle", 0.01);

const colorKeys = computed({
  get: () => ((block.value?.colorKeys ?? []) as string[]).join(", "),
  set: (value: string) => patch({ colorKeys: value.split(",").map((item) => item.trim()).filter(Boolean) }),
});

const availableKeys = computed(() => {
  const first = report.data?.[0];
  return first && typeof first === "object" ? Object.keys(first) : [];
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:chart-pie" accent="reporting">
    <EditorField :label="t('dataKeyLabel')" :hint="t('dataKeyHint')">
      <Select :model-value="dataKey" @update:model-value="(value) => (dataKey = value as string)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="">{{ t("rootData") }}</SelectItem>
          <SelectItem v-for="key in availableKeys" :key="key" :value="key">{{ key }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('labelKeyLabel')">
      <Input v-model="labelKey" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('valueKeyLabel')">
      <Input v-model="valueKey" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('colorKeysLabel')" :hint="t('colorKeysHint')">
      <Input v-model="colorKeys" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <div class="grid grid-cols-2 gap-2">
      <EditorField :label="t('innerRadiusLabel')">
        <Input v-model.number="innerRadius" type="number" min="0" class="h-8 rounded-sm bg-muted text-[13px]" />
      </EditorField>
      <EditorField :label="t('padAngleLabel')">
        <Input v-model.number="padAngle" type="number" step="0.01" min="0" class="h-8 rounded-sm bg-muted text-[13px]" />
      </EditorField>
    </div>

    <EditorField :label="t('displayLabel')">
      <div class="space-y-2 text-[12.5px] text-muted-foreground">
        <label class="flex items-center justify-between gap-3"><span>{{ t("showLegend") }}</span><Switch v-model="showLegend" /></label>
        <label class="flex items-center justify-between gap-3"><span>{{ t("showLabels") }}</span><Switch v-model="showLabels" /></label>
        <label class="flex items-center justify-between gap-3"><span>{{ t("showTotal") }}</span><Switch v-model="showTotal" /></label>
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Donut Chart"
  dataKeyLabel: "Data Key"
  dataKeyHint: "Choose the nested array to chart, or root data."
  rootData: "Root data"
  labelKeyLabel: "Label Key"
  valueKeyLabel: "Value Key"
  colorKeysLabel: "Color Keys"
  colorKeysHint: "Comma-separated color values or data keys."
  innerRadiusLabel: "Inner Radius"
  padAngleLabel: "Pad Angle"
  displayLabel: "Display"
  showLegend: "Show Legend"
  showLabels: "Show Labels"
  showTotal: "Show Total"
</i18n>
